import { inject, Injectable } from '@angular/core';
import { CollectionRequestBuilder, HttpErrorService } from '@web/common';
import {
  BooleanTypeSelectItemsProviderService,
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
  UploadException,
} from '@web/shared/ui';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  tap,
  throwError,
} from 'rxjs';
import { AdminMultimediaDataSourceService } from '@web/shared/module/multimedia';
import { SummernoteImageUpload } from '@web/shared/external';
import { Guid, once, valueOrDefault } from '@web/utils';
import { AdminBoxSaleApiService } from '../api/services/admin-box-sale-api.service';
import { AdminBoxSaleCollectionFilter } from '../models/admin-box-sale-collection-filter';
import { AdminBoxSaleCollectionItem } from '../models/admin-box-sale-collection-item';
import { AdminBoxSaleApproveStatus } from '../models/admin-box-sale-approve-status';
import { AdminBoxSaleApproveStatusOrders } from '../models/admin-box-sale-approve-status-orders';
import { AdminBoxSaleDetails } from '../models/admin-box-sale-details';
import { AdminBoxSaleVariant } from '../models/admin-box-sale-variant';
import {
  AdminBoxSaleDetailsForm,
  AdminBoxSaleGroups,
} from '../models/admin-box-sale-details-form';
import { AdminBoxSaleGroupCosmetic } from '../models/admin-box-sale-group-cosmetic';
import { AdminBoxSaleUpdateRequest } from '../api/requests/admin-box-sale-update.request';
import { AdminBoxSaleGroupRequest } from '../api/requests/admin-box-sale-group.request';
import { AdminBoxSaleGroupCosmeticRequest } from '../api/requests/admin-box-sale-group-cosmetic.request';

@Injectable({
  providedIn: 'root',
})
export class AdminBoxSaleDataSourceService {
  private readonly api = inject(AdminBoxSaleApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);
  private readonly multimediaDataSource = inject(
    AdminMultimediaDataSourceService,
  );
  private readonly httpError = inject(HttpErrorService);
  /* prettier-ignore */
  private readonly booleanTypeSelectItemsProvider = inject(BooleanTypeSelectItemsProviderService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminBoxSaleCollectionFilter>,
  ): Observable<TableDataPayload<AdminBoxSaleCollectionItem>> {
    return this.api
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('boxName').build(),
              )
              .appendText((builder) => builder.equalFor('status').build())
              .appendBoolean((builder) =>
                builder.equalFor('isContentPublic').build(),
              )
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  new AdminBoxSaleCollectionItem({
                    boxId: item.box.id,
                    boxName: item.box.name,
                    canBeApproved: item.contextOperations.canBeApproved,
                    canBeArchived: item.contextOperations.canBeArchived,
                    canBeBoxViewed: item.contextOperations.canBeBoxViewed,
                    canBeEdited: item.contextOperations.canBeEdited,
                    canBeGroupsEdited: item.contextOperations.canBeGroupsEdited,
                    canBeRemoved: item.contextOperations.canBeDeleted,
                    canBeViewed: item.contextOperations.canBeViewed,
                    canHideContent: item.contextOperations.canHideContent,
                    canPublicContent: item.contextOperations.canPublicContent,
                    createdAt: item.createdAt,
                    featuredFileAlt: item.featuredFile?.alt(),
                    featuredFileUrl: item.featuredFile?.url,
                    id: item.id,
                    isContentPublic: item.isContentPublic,
                    status: item.status,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchDetailsFormById(id: Guid): Observable<AdminBoxSaleDetailsForm> {
    return this.api.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new AdminBoxSaleDetailsForm({
            validityHours: response.validityHours,
            description: response.description,
            canBeGroupsEdited: response.contextOperations.canBeGroupsEdited,
            groups: new AdminBoxSaleGroups({
              items: response.groups.map((group) => ({
                cosmetics: group.cosmetics.map(
                  (cosmetic) =>
                    new AdminBoxSaleGroupCosmetic({
                      availableQuantity: cosmetic.availableQuantity,
                      amount: valueOrDefault(cosmetic.amount, 1),
                      cosmeticBrandName: cosmetic.cosmeticBrand?.name,
                      featureFileAlt: cosmetic.featureFile?.alt(),
                      featureFileUrl: cosmetic.featureFile?.url,
                      id: cosmetic.id,
                      name: cosmetic.name,
                    }),
                ),
                description: group.description,
                id: group.id,
                isVisibleInQuestionnaire: group.isVisibleInQuestionnaire,
                missingItemPickStrategy: group.missingItemPickStrategy,
              })),
            }),
            id: response.id,
          }),
      ),
    );
  }

  public fetchApproveStatusById(
    id: Guid,
  ): Observable<AdminBoxSaleApproveStatus> {
    return this.api.fetchApproveStatusById(id).pipe(
      map(
        (response) =>
          new AdminBoxSaleApproveStatus({
            errorMessage: response.errorMessage,
            isAvailable: response.isAvailable,
            questionnairesWillBeGenerated:
              response.questionnairesWillBeGenerated,
            orders: response.orders
              ? new AdminBoxSaleApproveStatusOrders({
                  paid: response.orders.paid,
                  partiallyCompleted: response.orders.partiallyCompleted,
                  partiallyRefunded: response.orders.partiallyRefunded,
                })
              : null,
          }),
      ),
    );
  }

  public fetchDetailsById(id: Guid): Observable<AdminBoxSaleDetails> {
    return this.api.fetchDetailsById(id).pipe(
      map(
        (response) =>
          new AdminBoxSaleDetails({
            description: response.description,
            groups: new AdminBoxSaleGroups({
              items: response.groups.map((group) => ({
                cosmetics: group.cosmetics.map(
                  (cosmetic) =>
                    new AdminBoxSaleGroupCosmetic({
                      availableQuantity: cosmetic.availableQuantity,
                      amount: cosmetic.amount,
                      remainingQuantity: cosmetic.remainingQuantity,
                      cosmeticBrandName: cosmetic.cosmeticBrand?.name,
                      featureFileAlt: cosmetic.featureFile?.alt(),
                      featureFileUrl: cosmetic.featureFile?.url,
                      id: cosmetic.id,
                      name: cosmetic.name,
                    }),
                ),
                description: group.description,
                id: group.id,
                isVisibleInQuestionnaire: group.isVisibleInQuestionnaire,
                missingItemPickStrategy: group.missingItemPickStrategy,
              })),
            }),
            id: response.id,
            validityHours: response.validityHours,
            variants: response.variants.map(
              (variant) =>
                new AdminBoxSaleVariant({
                  cosmetics: variant.cosmetics.map(
                    (cosmetic) =>
                      new AdminBoxSaleGroupCosmetic({
                        amount: cosmetic.amount,
                        cosmeticBrandName: cosmetic.cosmeticBrand?.name,
                        featureFileAlt: cosmetic.featureFile?.alt(),
                        featureFileUrl: cosmetic.featureFile?.url,
                        id: cosmetic.id,
                        name: cosmetic.name,
                      }),
                  ),
                  id: variant.id,
                  name: variant.name,
                }),
            ),
          }),
      ),
    );
  }

  public updateByFormData(
    id: Guid,
    data: AdminBoxSaleDetailsForm,
  ): Observable<void> {
    return this.api.update(
      id,
      new AdminBoxSaleUpdateRequest({
        validityHours: data.validityHours,
        description: data.description,
        groups: valueOrDefault(data.groups?.items, []).map(
          (group) =>
            new AdminBoxSaleGroupRequest({
              cosmetics: group.cosmetics.map(
                (cosmetic) =>
                  new AdminBoxSaleGroupCosmeticRequest({
                    amount: valueOrDefault(cosmetic.amount, 1),
                    availableQuantity: cosmetic.availableQuantity,
                    id: cosmetic.id,
                  }),
              ),
              description: group.description,
              id: group.id,
              isVisibleInQuestionnaire: group.isVisibleInQuestionnaire,
              missingItemPickStrategy: group.missingItemPickStrategy,
            }),
        ),
      }),
    );
  }

  public confirmAndRemoveById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz usunąć wybraną konfigurację sprzedaży boxa kosmetycznego?',
          )
          .accept(() => {
            this.api
              .removeById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Wybrana konfiguracja sprzedaży boxa kosmetycznego została usunięta.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public approveById(id: Guid): Observable<void> {
    return this.api.approveById(id).pipe(
      tap(() => {
        this.toast.show((builder) =>
          builder
            .initSuccess(
              'Konfiguracja sprzedaży wybranego boxa kosmetycznego została zatwierdzona.',
            )
            .build(),
        );
      }),
    );
  }

  public confirmAndArchiveById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Zarchiwizowanie tej opcji wyłączy dalsze generowanie ankiet dla tej edycji boxa.',
          )
          .accept(() => {
            this.api
              .archiveById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Wybrana konfiguracja sprzedaży boxa kosmetycznego została zarchiwizowana.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndPublicContentById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz upublicznić zawartość wybranego boxa kosmetycznego?',
          )
          .accept(() => {
            this.api
              .publicContentById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Zawartość wybranego boxa kosmetycznego została upubliczniona.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndHideContentById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz ukryć zawartość wybranego boxa kosmetycznego?',
          )
          .accept(() => {
            this.api
              .hideContentById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Zawartość wybranego boxa kosmetycznego została ukryta.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public uploadEditorImages(
    files: File[],
  ): Observable<SummernoteImageUpload[]> {
    return combineLatest(
      files.map((file) =>
        this.multimediaDataSource
          .upload(file)
          .pipe(
            catchError((response) =>
              throwError(
                () =>
                  new UploadException(
                    this.httpError.extract(response).multiLineMessages(),
                  ),
              ),
            ),
          ),
      ),
    ).pipe(
      map((items) =>
        items.map(
          (item) => new SummernoteImageUpload(item.previewUrl, item.filename),
        ),
      ),
    );
  }

  public booleanSelectItems() {
    return this.booleanTypeSelectItemsProvider.selectItems();
  }
}
